import React, { useState, useEffect, useMemo } from 'react'
import './trade-portal-offer-modal.styles.scss'
import Modal from '@/components/modal/modal.component'
import { ModalAlert } from '@/components/modal/modal.component'
import { UseCloudGameTransactions } from '@/utils/firebase.utils'
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark } from '@fortawesome/free-solid-svg-icons'

const TRADEABLE_TYPES = ['inventory', 'collectible', 'lottery']

const TradePortalOfferModal = ({ show, cancel, post, gameData, playerData, gameElements, onSuccess }) => {
    const [offerItems, setOfferItems] = useState([])
    const [addingItem, setAddingItem] = useState(false)
    const [addType, setAddType] = useState('item')
    const [addItem, setAddItem] = useState(null)
    const [addQty, setAddQty] = useState(1)
    const [loading, setLoading] = useState(false)
    const [alertMessage, setAlertMessage] = useState(null)

    useEffect(() => {
        if (!show) {
            setOfferItems([])
            setAddingItem(false)
            setAddType('item')
            setAddItem(null)
            setAddQty(1)
        }
    }, [show])

    const availableItems = useMemo(() => {
        if (!playerData?.items || !gameElements) return []
        return Object.entries(playerData.items)
            .filter(([itemId, itemData]) => {
                const el = gameElements[itemId]
                return el && TRADEABLE_TYPES.includes(el.opt_itemType) && itemData.count > 0
            })
            .map(([itemId, itemData]) => ({
                itemId,
                el: gameElements[itemId],
                count: itemData.count,
            }))
            .sort((a, b) => a.el.name.localeCompare(b.el.name))
    }, [playerData?.items, gameElements])

    function getMaxForAddItem() {
        if (!addItem) return 1
        const owned = playerData?.items?.[addItem.id]?.count ?? 0
        const alreadyOffered = offerItems
            .filter((o) => o.itemId === addItem.id)
            .reduce((sum, o) => sum + o.quantity, 0)
        return Math.max(0, owned - alreadyOffered)
    }

    function handleAddItem() {
        if (addType === 'currency') {
            if (addQty < 1 || addQty > (playerData?.currency ?? 0)) return
            setOfferItems((prev) => [...prev, { type: 'currency', itemId: null, quantity: addQty }])
        } else if (addType === 'points') {
            if (addQty < 1 || addQty > (playerData?.points ?? 0)) return
            setOfferItems((prev) => [...prev, { type: 'points', itemId: null, quantity: addQty }])
        } else {
            if (!addItem || addQty < 1) return
            setOfferItems((prev) => [
                ...prev,
                { type: addItem.opt_itemType, itemId: addItem.id, quantity: addQty },
            ])
        }
        setAddItem(null)
        setAddQty(1)
        setAddingItem(false)
    }

    function getItemLabel(offerItem) {
        if (offerItem.type === 'currency') return `${offerItem.quantity} Currency`
        if (offerItem.type === 'points') return `${offerItem.quantity} Points`
        const el = gameElements?.[offerItem.itemId]
        const name = el?.name ?? offerItem.itemId
        return offerItem.quantity > 1 ? `${offerItem.quantity}x ${name}` : name
    }

    function getRequestLabel(req) {
        if (req.type === 'currency') return `${req.quantity} Currency`
        if (req.type === 'points') return `${req.quantity} Points`
        const el = gameElements?.[req.itemId]
        const name = el?.name ?? req.itemId
        return req.quantity > 1 ? `${req.quantity}x ${name}` : name
    }

    const offeringEl = gameElements?.[post?.offering?.itemId]

    async function handleSubmit() {
        setLoading(true)
        const res = await UseCloudGameTransactions('submitTradeOffer', {
            gameId: gameData.gameId,
            playerId: playerData.playerId,
            tradeId: post.id,
            items: offerItems,
        })
        setLoading(false)
        if (res?.error) {
            setAlertMessage(res.error)
            return
        }
        onSuccess?.()
    }

    return (
        <Modal show={show} cancel={cancel} text='Make an Offer' closeButton={true}>
            <ModalAlert
                show={!!alertMessage}
                cancel={() => setAlertMessage(null)}
                message={alertMessage}
            />
            <div className='trade-modal-content'>

                {/* What the poster is offering */}
                <div className='form-section'>
                    <span className='form-label'>They are offering:</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {offeringEl?.icon && (
                            <img src={offeringEl.icon} alt={offeringEl.name} style={{ width: 28, height: 28, objectFit: 'contain' }} />
                        )}
                        <span>
                            {post?.offering?.quantity > 1 ? `${post.offering.quantity}x ` : ''}
                            {offeringEl?.name ?? 'Item'}
                        </span>
                    </div>
                </div>

                {/* What the poster wants */}
                {post?.requesting?.length > 0 && (
                    <div className='form-section'>
                        <span className='form-label'>They are asking for:</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                            {post.requesting.map((req, i) => (
                                <span key={i} className='want-chip' style={{ padding: '0.18rem 0.5rem', background: '#e8f0fe', borderRadius: '1rem', fontSize: '0.78rem' }}>
                                    {getRequestLabel(req)}
                                </span>
                            ))}
                        </div>
                        <span className='form-hint'>Your offer does not need to match exactly — the poster decides.</span>
                    </div>
                )}

                {/* Build your offer */}
                <div className='form-section'>
                    <span className='form-label'>Your offer:</span>
                    <span className='form-hint'>Leave empty to make a no-cost offer.</span>

                    {offerItems.length > 0 && (
                        <div className='request-list'>
                            {offerItems.map((item, i) => (
                                <div key={i} className='request-row'>
                                    <span className='request-label'>{getItemLabel(item)}</span>
                                    <button
                                        className='remove-req-btn'
                                        onClick={() =>
                                            setOfferItems((prev) => prev.filter((_, j) => j !== i))
                                        }
                                    >
                                        <FontAwesomeIcon icon={faXmark} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {!addingItem && (
                        <button
                            className='g-button small-btn'
                            onClick={() => setAddingItem(true)}
                        >
                            + Add Item
                        </button>
                    )}

                    {addingItem && (
                        <div className='add-request-panel'>
                            <div className='form-section'>
                                <span className='form-label'>Type:</span>
                                <div className='field small'>
                                    <select
                                        className='uk-select'
                                        value={addType}
                                        onChange={(e) => {
                                            setAddType(e.target.value)
                                            setAddItem(null)
                                        }}
                                    >
                                        <option value='item'>Item</option>
                                        <option value='currency'>
                                            Currency (have: {playerData?.currency ?? 0})
                                        </option>
                                        <option value='points'>
                                            Points (have: {playerData?.points ?? 0})
                                        </option>
                                    </select>
                                </div>
                            </div>

                            {addType === 'item' && (
                                <div className='item-grid'>
                                    {availableItems.length === 0 ? (
                                        <span className='no-items-msg'>No tradeable items.</span>
                                    ) : (
                                        availableItems.map(({ itemId, el, count }) => (
                                            <button
                                                key={itemId}
                                                className={`item-chip ${addItem?.id === el.id ? 'selected' : ''}`}
                                                onClick={() => setAddItem(el)}
                                            >
                                                {el.icon && <img src={el.icon} alt={el.name} />}
                                                {el.name}
                                                <span className='chip-sub'>(have {count})</span>
                                            </button>
                                        ))
                                    )}
                                </div>
                            )}

                            <div className='form-section'>
                                <span className='form-label'>Qty:</span>
                                <div className='field small'>
                                    <input
                                        type='number'
                                        className='uk-input'
                                        min={1}
                                        max={
                                            addType === 'currency'
                                                ? playerData?.currency ?? 0
                                                : addType === 'points'
                                                ? playerData?.points ?? 0
                                                : getMaxForAddItem()
                                        }
                                        value={addQty}
                                        onChange={(e) =>
                                            setAddQty(Math.max(1, Number(e.target.value)))
                                        }
                                    />
                                </div>
                            </div>

                            <div className='panel-actions'>
                                <button
                                    className='g-button small-btn'
                                    onClick={() => setAddingItem(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className='g-button small-btn primary'
                                    disabled={addType === 'item' && !addItem}
                                    onClick={handleAddItem}
                                >
                                    Add
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className='modal-footer'>
                    <button className='g-button med-btn' onClick={cancel}>
                        Cancel
                    </button>
                    <button
                        className='g-button med-btn primary'
                        disabled={loading}
                        onClick={handleSubmit}
                    >
                        {loading ? <ThreeDotsLoader /> : 'Submit Offer'}
                    </button>
                </div>
            </div>
        </Modal>
    )
}

export default TradePortalOfferModal
